import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BrickWall, Loader2, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RelativeTime } from "@/components/RelativeTime";

interface FirewallRule {
  id: string;
  ruleType: "ip_allow" | "ip_block" | "rate_limit";
  cidr: string | null;
  rpsLimit: number | null;
  windowSec: number | null;
  enabled: boolean;
}

interface FirewallResponse {
  rules: FirewallRule[];
  activity: {
    last24h: Record<string, number>;
    recent: Array<{ id: string; ts: string; ip: string; action: "blocked" | "rate_limited"; path: string | null }>;
  };
}

/**
 * 006 US9 — firewall section (rendered on the Protection tab). IP
 * allow/block + one per-client rate limit, enforced at the edge. Activity
 * counts are sampled (a floor, not exact) — labeled as such.
 */
export function FirewallSection({ orgSlug, projectSlug }: { orgSlug: string; projectSlug: string }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<"ip_block" | "ip_allow" | "rate_limit">("ip_block");
  const [cidr, setCidr] = useState("");
  const [rps, setRps] = useState("60");
  const [windowSec, setWindowSec] = useState("60");

  const q = useQuery({
    queryKey: ["firewall", orgSlug, projectSlug],
    queryFn: () => api.get<FirewallResponse>(`/orgs/${orgSlug}/projects/${projectSlug}/firewall`),
    refetchInterval: 30_000,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post(`/orgs/${orgSlug}/projects/${projectSlug}/firewall`, {
        ruleType: kind,
        ...(kind === "rate_limit"
          ? { rpsLimit: Number(rps), windowSec: Number(windowSec) }
          : { cidr }),
      }),
    onSuccess: () => {
      setCidr("");
      qc.invalidateQueries({ queryKey: ["firewall", orgSlug, projectSlug] });
    },
  });
  const patch = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      api.patch(`/orgs/${orgSlug}/projects/${projectSlug}/firewall/${v.id}`, { enabled: v.enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["firewall", orgSlug, projectSlug] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${orgSlug}/projects/${projectSlug}/firewall/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["firewall", orgSlug, projectSlug] }),
  });

  const blocked = q.data?.activity.last24h.blocked ?? 0;
  const limited = q.data?.activity.last24h.rate_limited ?? 0;

  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <BrickWall className="h-3.5 w-3.5 text-muted-foreground" /> Firewall
        </h3>
        <span className="text-2xs text-subtle-foreground">
          last 24h: ≥{blocked} blocked · ≥{limited} rate-limited
        </span>
      </div>

      {q.isLoading && <div className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</div>}
      {!q.isLoading && (q.data?.rules.length ?? 0) === 0 && (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          No firewall rules. With none configured this project's traffic path is completely untouched.
        </div>
      )}

      {q.data?.rules.map((r) => (
        <div key={r.id} className="of-row gap-3 items-center">
          <span
            className={
              "text-2xs uppercase tracking-wider font-mono rounded px-1.5 py-0.5 border " +
              (r.ruleType === "ip_block"
                ? "bg-danger-subtle text-danger border-danger/30"
                : r.ruleType === "ip_allow"
                  ? "bg-success-subtle text-success border-success/30"
                  : "bg-attention-subtle text-attention border-attention/30")
            }
          >
            {r.ruleType.replace("_", " ")}
          </span>
          <span className="font-mono text-xs flex-1 min-w-0 truncate">
            {r.ruleType === "rate_limit" ? `${r.rpsLimit} req / ${r.windowSec ?? 60}s per IP` : r.cidr}
          </span>
          <label className="text-xs flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={r.enabled}
              onChange={(e) => patch.mutate({ id: r.id, enabled: e.target.checked })}
            />
            enabled
          </label>
          <Button
            variant="ghost"
            size="icon"
            title="Delete rule"
            onClick={() => {
              if (window.confirm("Delete this firewall rule?")) del.mutate(r.id);
            }}
          >
            {del.isPending && del.variables === r.id ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5 text-danger" />
            )}
          </Button>
        </div>
      ))}

      <div className="px-4 py-3 border-t border-border-muted space-y-2">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">Rule</Label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as typeof kind)}
              className="h-8 px-2 rounded-md border border-border bg-background text-sm font-mono"
            >
              <option value="ip_block">block IPs</option>
              <option value="ip_allow">allow only IPs</option>
              <option value="rate_limit">rate limit</option>
            </select>
          </div>
          {kind === "rate_limit" ? (
            <>
              <div className="space-y-1">
                <Label className="text-xs">Max requests</Label>
                <Input value={rps} onChange={(e) => setRps(e.target.value)} type="number" min={1} className="h-8 w-28 font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Window (sec)</Label>
                <Input value={windowSec} onChange={(e) => setWindowSec(e.target.value)} type="number" min={1} max={3600} className="h-8 w-28 font-mono" />
              </div>
            </>
          ) : (
            <div className="space-y-1 flex-1 min-w-[200px]">
              <Label className="text-xs">IP or CIDR</Label>
              <Input value={cidr} onChange={(e) => setCidr(e.target.value)} placeholder="203.0.113.0/24" className="h-8 font-mono text-xs" />
            </div>
          )}
          <Button
            variant="default"
            size="sm"
            disabled={create.isPending || (kind !== "rate_limit" && !cidr)}
            onClick={() => create.mutate()}
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add rule
          </Button>
        </div>
        {kind === "ip_allow" && (
          <div className="text-2xs text-attention">
            Allow-list mode rejects every IP not matched by an allow rule — make sure your own address is covered.
          </div>
        )}
        {create.error instanceof ApiError && <div className="text-xs text-danger">{create.error.message}</div>}
      </div>

      {(q.data?.activity.recent.length ?? 0) > 0 && (
        <div className="border-t border-border-muted">
          <div className="px-4 py-2 text-2xs uppercase tracking-wider text-subtle-foreground">
            Recent activity (sampled)
          </div>
          {q.data!.activity.recent.slice(0, 10).map((e) => (
            <div key={e.id} className="px-4 py-1.5 flex flex-wrap items-center gap-3 text-xs border-t border-border-muted/60">
              <span className={e.action === "blocked" ? "text-danger" : "text-attention"}>
                {e.action === "blocked" ? "blocked" : "limited"}
              </span>
              <span className="font-mono">{e.ip}</span>
              <span className="font-mono text-subtle-foreground truncate flex-1 min-w-0">{e.path}</span>
              <span className="text-subtle-foreground">
                <RelativeTime value={e.ts} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
